import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type Table,
  useReactTable,
} from "@tanstack/react-table";
import { toast } from "sonner";
import {
  BadgeCheck,
  ChartNoAxesColumnIncreasing,
  Check,
  Eye,
  Info,
  ScanEye,
  Trash2,
  TriangleAlert,
  Zap,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  authAPI,
  billingAPI,
  promptsAPI,
  type PromptCluster,
  type SuggestedPromptsGenerateBlockedReason,
  type SuggestedPromptsGenerateMode,
  type SuggestedPromptsGenerateResponse,
} from "../services/api";
import { getSuggestedColumns, PromptsPageColumns } from "../services/helpers";
import type { Brand } from "@/components/AppSidebar";
import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import { ExtendedTable } from "@/components/ExtendedTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PromptsTable from "../components/PromptsTable";
import PromptsDetails from "./PromptsDetails";
import PromptsActive from "@/components/PromptsActive";
import PromptsInactive from "@/components/PromptsInactive";
import { useEffect, useRef, useState } from "react";
import AddPromptsDialog from "@/components/AddPromptsDialog";
import { Badge } from "@/components/ui/badge";
import SearchExportGenerate from "@/components/SearchExportGenerate";
import ActionsSuggested from "@/components/ActionsSuggested";
import { useDataStore } from "@/store/useDataStore";
import { useAuth } from "@/components/AuthContext";
import { usePromptsRealtime } from "@/hooks/usePromptsRealtime";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Separator } from "@/components/ui/separator";
import type { DateRangeValue } from "@/components/DateRangeFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ClusterAction } from "@/components/ClusterActions";
import { ClusterAdd } from "../components/ClusterAdd";
import repeatIcon from "@/assets/repeat.svg";
import viewTasksArrowRight from "@/assets/view-tasks-arrow-right.svg";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
  toPlatformValue,
} from "@/lib/posthog";
import {
  getCooldownBlockedMessage,
  getPromptRunsBlockedMessage,
  getTrialBoostBlockedInfo,
  shouldBlockBoostByCooldown,
} from "@/lib/clusterBoost";
import { resolvePlanType } from "@/lib/planTier";
import {
  isPromptMetricLoading,
  isQueuedOrRunningPrompt,
} from "./promptsMetricLoading";
import "./style.css";

export type Prompt = {
  country: string;
  text: string;
  is_active: boolean;
  id: string;
  brand: string;
  cluster?: string;
  created_at: Date;
  updated_at: Date;
  last_run_status?: string | null;
  provider_statuses?: Record<string, string> | null;
  has_provider_run_in_progress?: boolean | null;
  visibility?: number | null;
  position?: number | null;
  positionsByRun?: {
    runId: string;
    positions: Record<string, number>;
    isMainMentioned?: boolean;
  }[];
};

export type Addprompt = {
  country: string;
  text: string;
  id?: string;
  suggestedPromptId?: string;
  isActive: boolean;
  brandId: string;
  clusterId?: string;
  clusterTitle?: string;
};

type SuggestedPrompt = {
  id?: string;
  text: string;
  country: string;
  clusterId?: string | null;
};

type SuggestionUiLocation = "prompts_page" | "empty_state" | "onboarding_guide";

type SuggestionBlockedState = {
  reason: SuggestedPromptsGenerateBlockedReason;
  remainingQuota: number;
  capToday: number;
  generatedToday: number;
};

const CHUNK_SIZE = 6;
const BOOST_RUNNING_STATUSES = new Set(["generating", "queued"]);

const isClusterActivelyRunningBoost = (
  cluster?: PromptCluster | null,
): boolean => {
  const status = String(cluster?.boostStatus || "")
    .trim()
    .toLowerCase();
  return BOOST_RUNNING_STATUSES.has(status);
};

const getRunningPromptClusterId = (
  clusters: PromptCluster[],
): string | null => {
  const runningCluster = clusters.find((cluster) =>
    isClusterActivelyRunningBoost(cluster),
  );
  return runningCluster?.id || null;
};

const getScopeKey = (
  mode: SuggestedPromptsGenerateMode,
  clusterId?: string | null,
) =>
  mode === "single_cluster"
    ? `single_cluster:${clusterId || ""}`
    : "all_clusters";

const getBlockedTooltipMessage = (
  reason?: SuggestedPromptsGenerateBlockedReason | null,
) => {
  if (reason === "daily_cap_reached") {
    return "You\u2019ve reached today\u2019s suggestion limit. Come back tomorrow.";
  }
  if (reason === "cluster_daily_cap_reached") {
    return "You\u2019ve reached today\u2019s limit for this cluster. Come back tomorrow.";
  }
  if (reason === "plan_quota_reached") {
    return "You\u2019ve reached your plan prompt limit. Upgrade to add more prompts.";
  }
  if (reason === "no_clusters") {
    return "Add a cluster first to generate suggested prompts.";
  }
  return null;
};

const defaultSuggestedData: SuggestedPrompt[] = [];
const ALLOWED_BOOST_DAYS = new Set([1, 7, 14, 30, 90]);

const resolveBoostDays = (timeRange?: string, fallback = 30): number => {
  const parsed = Number.parseInt(
    String(timeRange || "").replace(/d$/i, ""),
    10,
  );
  if (ALLOWED_BOOST_DAYS.has(parsed)) {
    return parsed;
  }
  return fallback;
};

export default function PromptsPage() {
  const { t } = useLocale();
  const {
    currentBrand,
    selectedCountries,
    selectedProviders,
    getCountriesOption,
    timeRange,
    setTimeRange,
    dateRange,
    setDateRange,
  } = useOutletContext<{
    currentBrand: Brand;
    isTrialEnded: boolean;
    getCountriesOption: (c: never) => never[];
    selectedCountries: string[];
    selectedProviders?: string[];
    timeRange?: string;
    setTimeRange?: (value: string) => void;
    dateRange?: DateRangeValue;
    setDateRange?: (value: DateRangeValue) => void;
  }>();
  const resolvedBrandId =
    (currentBrand as any)?.brandId || (currentBrand as any)?.id;
  const resolvedDays = resolveBoostDays(timeRange, 30);
  const promptDateRange =
    dateRange?.preset === "custom" && dateRange.startDate && dateRange.endDate
      ? {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }
      : null;
  const promptDateRangeKey = promptDateRange
    ? `${promptDateRange.startDate}:${promptDateRange.endDate}`
    : "rolling";
  const providersKey = selectedProviders?.join(",") || "all";
  const promptsQueryKey = [
    "prompts",
    resolvedBrandId,
    selectedCountries,
    providersKey,
    resolvedDays,
    promptDateRangeKey,
  ];
  const shouldUsePromptsRealtime = !!resolvedBrandId;

  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [inactiveRowSelection, setInactiveRowSelection] = useState({});
  const [openAddPromptDialog, setOpenAddPromptDialog] = useState(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [suggestedPromptsRowSelection, setSuggestedPromptsRowSelection] =
    useState({});
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const requested = searchParams.get("tab");
    return requested === "suggested" || requested === "inactive"
      ? requested
      : "active";
  });
  const { storedLimitPrompts, setStoredLimitPrompts, rawData, setRawData } =
    useDataStore();
  const [activePagination, setActivePagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [inactivePagination, setInactivePagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [suggestedPagination, setSuggestedPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectedCluster, setSelectedCluster] = useState("all-clusters");
  const [blockedByScope, setBlockedByScope] = useState<
    Record<string, SuggestionBlockedState>
  >({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [detailsPromptId, setDetailsPromptId] = useState<string | null>(null);
  const [isPromptDetailsOpen, setIsPromptDetailsOpen] = useState(false);
  const promptsListTrackedRef = useRef<string>("");
  const promptsDashboardTrackedRef = useRef<string>("");
  const guidePromptId = searchParams.get("guidePromptId");

  // const [animating, setAnimating] = useState(false);
  const [pendingBoostClusterId, setPendingBoostClusterId] = useState<
    string | null
  >(null);
  const [pendingBoostBrandId, setPendingBoostBrandId] = useState<string | null>(
    null,
  );
  const [isBoostLimitModalOpen, setIsBoostLimitModalOpen] = useState(false);
  const navigate = useNavigate();
  const buildClusterActionsPath = React.useCallback(
    (
      brandId: string | null | undefined,
      clusterId: string | null | undefined,
    ) => {
      const brandSearch = brandId ? `?brandId=${brandId}` : "";
      const clusterSearch = clusterId
        ? `${brandId ? "&" : "?"}cluster=${clusterId}`
        : "";
      return `/dashboard/get-cited/cluster-actions${brandSearch}${clusterSearch}`;
    },
    [],
  );

  useEffect(() => {
    const requestedCluster = searchParams.get("cluster");
    setSelectedCluster(requestedCluster || "all-clusters");
    const requestedTab = searchParams.get("tab");
    if (
      requestedTab === "active" ||
      requestedTab === "suggested" ||
      requestedTab === "inactive"
    ) {
      setTab(requestedTab);
    }
  }, [searchParams]);

  const resetDataStore = useDataStore((s) => s.reset);
  const { user } = useAuth();
  const resolvedOrgId =
    ((user as any)?.company?.id as string | undefined) ||
    ((user as any)?.companyId as string | undefined) ||
    null;
  const { data: billingSubscription, isFetched: billingSubscriptionFetched } =
    useQuery({
      queryKey: ["billing-subscription", (user as any)?.id || "unknown"],
      queryFn: () => billingAPI.getSubscription(),
      enabled: Boolean((user as any)?.id),
      staleTime: 60000,
    });
  const resolvedPlanType = resolvePlanType(
    user?.tier,
    billingSubscription?.subscription_status,
  );
  const normalizedUserTier = String((user as any)?.tier || "")
    .trim()
    .toLowerCase();
  const isStarterTierToken =
    normalizedUserTier === "1" || normalizedUserTier === "starter";
  const planType =
    isStarterTierToken && !billingSubscriptionFetched
      ? "paid"
      : resolvedPlanType;
  const boostEventSource =
    searchParams.get("guideTarget") === "prompts-boost-button"
      ? "onboarding"
      : "prompts_page";
  //---start-requests
  const { data, isFetching, isLoading } = useQuery({
    queryKey: promptsQueryKey,
    queryFn: async () => {
      if (!resolvedBrandId || !currentBrand) return [];
      const res = await promptsAPI.getPrompts(
        { ...currentBrand, brandId: resolvedBrandId },
        selectedCountries,
        selectedProviders,
        resolvedDays,
        promptDateRange,
      );
      return Array.isArray(res?.clusters) ? res.clusters : [];
    },
    enabled: !!resolvedBrandId,
    staleTime: Infinity,
  });

  const { data: limitPrompts = storedLimitPrompts } = useQuery({
    queryKey: ["limits", user?.id, resolvedBrandId],
    queryFn: async () => {
      const res = await authAPI.getMeUsage();
      return res;
    },
    initialData: storedLimitPrompts || undefined,
    staleTime: Infinity,
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [selectedCluster]);

  useEffect(() => {
    if (!selectedCountries || selectedCountries.length === 0) {
      setStoredLimitPrompts(limitPrompts);
    }
  }, [limitPrompts]);

  useEffect(() => {
    if (user?.id) {
      resetDataStore();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isBoostLimitModalOpen || !resolvedBrandId) return;
    capturePosthogEvent("upgrade_modal_viewed", {
      modal: "trial_boost_limit",
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
    });
  }, [isBoostLimitModalOpen, resolvedBrandId, resolvedOrgId]);

  const clustersData = React.useMemo<PromptCluster[]>(() => {
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const selectedClusterPromptRunStats = React.useMemo(() => {
    if (selectedCluster === "all-clusters") {
      return { total: 0, queuedOrRunning: 0, blocked: false };
    }
    const activeCluster = clustersData.find(
      (cluster) => cluster.id === selectedCluster,
    );
    const prompts = Array.isArray(activeCluster?.prompts)
      ? activeCluster.prompts
      : [];
    const activePrompts = prompts.filter(
      (prompt: any) => prompt?.is_active !== false,
    );
    const total = activePrompts.length;
    const queuedOrRunning = activePrompts.filter((prompt: any) =>
      isQueuedOrRunningPrompt(prompt),
    ).length;
    const blocked = total > 0 && queuedOrRunning / total > 0.5;
    return { total, queuedOrRunning, blocked };
  }, [clustersData, selectedCluster]);

  const selectedClusterMeta = React.useMemo(() => {
    if (selectedCluster === "all-clusters") return null;
    return (
      clustersData.find((cluster) => cluster.id === selectedCluster) || null
    );
  }, [clustersData, selectedCluster]);
  const trialAllowedClusterId = React.useMemo(
    () =>
      clustersData.find((cluster) => cluster?.trial_allowed_cluster_id)
        ?.trial_allowed_cluster_id || getRunningPromptClusterId(clustersData),
    [clustersData],
  );
  const isTrialLockedCluster = Boolean(
    planType === "trial" &&
      selectedCluster !== "all-clusters" &&
      trialAllowedClusterId &&
      selectedCluster &&
      trialAllowedClusterId !== selectedCluster,
  );
  const generateMode: SuggestedPromptsGenerateMode =
    selectedCluster === "all-clusters" ? "all_clusters" : "single_cluster";
  const generateClusterId =
    generateMode === "single_cluster" ? selectedCluster : null;
  const currentGenerateScopeKey = getScopeKey(generateMode, generateClusterId);
  const remainingQuotaRaw = Number((limitPrompts as any)?.prompts?.remaining);
  const hasRemainingQuota = Number.isFinite(remainingQuotaRaw);
  const remainingQuota = hasRemainingQuota
    ? Math.max(remainingQuotaRaw, 0)
    : Number.MAX_SAFE_INTEGER;
  const precomputedPlanBlockedState: SuggestionBlockedState | null =
    hasRemainingQuota && remainingQuota <= 0
      ? {
          reason: "plan_quota_reached",
          remainingQuota,
          capToday: 0,
          generatedToday: 0,
        }
      : null;
  const activeBlockedState =
    precomputedPlanBlockedState ||
    blockedByScope[currentGenerateScopeKey] ||
    null;
  const suggestGenerationDisabled = Boolean(activeBlockedState);
  const suggestGenerationTooltip = getBlockedTooltipMessage(
    activeBlockedState?.reason || null,
  );

  const boostBlockedByPromptRuns = selectedClusterPromptRunStats.blocked;
  const selectedClusterIsRegenerate =
    String(selectedClusterMeta?.boostStatus || "")
      .trim()
      .toLowerCase() === "ready";
  const boostBlockedByCooldown = shouldBlockBoostByCooldown(
    Boolean(selectedClusterMeta?.boostLocked),
    selectedClusterIsRegenerate,
  );
  const boostBlockedByPromptRunsReason = boostBlockedByPromptRuns
    ? getPromptRunsBlockedMessage(
        selectedClusterPromptRunStats.queuedOrRunning,
        selectedClusterPromptRunStats.total,
      )
    : "";
  const boostBlockedByCooldownReason = boostBlockedByCooldown
    ? getCooldownBlockedMessage(
        selectedClusterMeta?.boostUnlockAt,
        selectedClusterMeta?.boostCooldownDays,
      )
    : "";

  const promptsData = React.useMemo(() => {
    if (Array.isArray(data)) {
      return data.flatMap((cluster) => cluster.prompts || []);
    }
    return Array.isArray(rawData) ? rawData : [];
  }, [data, rawData]);
  const hasPromptRows = promptsData.length > 0;
  const shouldShowPromptsTableLoading =
    (isLoading || isFetching) && !hasPromptRows;

  const computedData = React.useMemo(() => promptsData, [promptsData]);
  const selectedPromptForDetails = React.useMemo(() => {
    if (!detailsPromptId) return null;
    return computedData.find((prompt) => prompt.id === detailsPromptId) || null;
  }, [computedData, detailsPromptId]);

  const isSamePrompts = React.useCallback(
    (nextData: Prompt[]) => {
      if (rawData === nextData) return true;
      if (!rawData || rawData.length !== nextData.length) return false;
      for (let i = 0; i < rawData.length; i += 1) {
        const prev = rawData[i];
        const next = nextData[i];
        if (prev?.id !== next?.id) return false;
        if (prev?.position !== next?.position) return false;
        if (prev?.visibility !== next?.visibility) return false;
      }
      return true;
    },
    [rawData],
  );

  useEffect(() => {
    if (!selectedCountries || selectedCountries.length === 0) {
      if (Array.isArray(promptsData) && !isSamePrompts(promptsData)) {
        setRawData(promptsData);
      }
    }
  }, [promptsData, isSamePrompts, selectedCountries, setRawData]);

  useEffect(() => {
    if (!resolvedBrandId) return;
    const filterCountry =
      selectedCountries.length === 1
        ? String(selectedCountries[0] || "")
            .trim()
            .toUpperCase()
        : "all";
    const filterCluster =
      selectedCluster === "all-clusters"
        ? "all"
        : clustersData.find((cluster) => cluster.id === selectedCluster)
            ?.title || "all";
    const filterPlatform = toPlatformValue(selectedProviders || []);
    const timeRangeValue = promptDateRange
      ? `${promptDateRange.startDate}:${promptDateRange.endDate}`
      : `${resolvedDays}d`;
    const key = `${resolvedBrandId}:${filterPlatform}:${filterCountry}:${filterCluster}:${timeRangeValue}`;
    if (promptsListTrackedRef.current === key) return;
    promptsListTrackedRef.current = key;
    capturePosthogEvent("prompts_list_viewed", {
      brand_id: resolvedBrandId,
      filter_platform: filterPlatform,
      filter_country: filterCountry || "all",
      filter_cluster: filterCluster || "all",
      time_range: timeRangeValue,
    });
  }, [
    clustersData,
    promptDateRange,
    resolvedBrandId,
    resolvedDays,
    selectedCluster,
    selectedCountries,
    selectedProviders,
  ]);

  useEffect(() => {
    if (!resolvedBrandId) return;
    const hasData = Array.isArray(promptsData) && promptsData.length > 0;
    const key = `${resolvedBrandId}:${hasData}`;
    if (promptsDashboardTrackedRef.current === key) return;
    promptsDashboardTrackedRef.current = key;
    capturePosthogEvent("dashboard_viewed", {
      brand_id: resolvedBrandId,
      dashboard: "prompts",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId: resolvedBrandId,
        dashboard: "prompts",
      }),
      has_data: hasData,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(resolvedBrandId) ?? undefined,
    });
  }, [promptsData, resolvedBrandId, user?.id]);

  const {
    data: suggestedPromptsData = defaultSuggestedData,
    isLoading: isSuggestedPromptsLoading,
    isFetching: isSuggestedPromptsFetching,
  } = useQuery<SuggestedPrompt[]>({
    queryKey: ["suggestedPrompts", resolvedBrandId],
    queryFn: async () => {
      if (!resolvedBrandId) return [];
      const res = await promptsAPI.getSuggestedPrompts(resolvedBrandId);
      return res.suggestedPrompts;
    },
    enabled: !!resolvedBrandId,
    staleTime: Infinity,
  });

  usePromptsRealtime({
    brandId: resolvedBrandId,
    countries: selectedCountries,
    providers: selectedProviders,
    days: resolvedDays,
    startDate: promptDateRange?.startDate,
    endDate: promptDateRange?.endDate,
    enabled: shouldUsePromptsRealtime,
    queryKey: promptsQueryKey,
  });
  const generatedPromptsQuery = useMutation({
    mutationFn: async ({
      brandId,
      selectedClusterId,
      mode,
    }: {
      brandId: string;
      selectedClusterId: string | null;
      mode: SuggestedPromptsGenerateMode;
    }) => {
      return promptsAPI.generatePrompts(
        brandId,
        selectedClusterId,
        mode,
        CHUNK_SIZE,
      );
    },
  });
  //---end-requests
  //--start--all mutations
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await promptsAPI.deletePrompt(id);
      return res.prompts;
    },
  });

  const deleteSuggestedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await promptsAPI.deleteSuggestedPrompt(id);
      return res.prompts;
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await promptsAPI.updatePromptStatus(data, data.id);
      return res.prompts;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (currentPrompt: Addprompt) => {
      const res = await promptsAPI.addPrompt(currentPrompt);
      return res.prompts;
    },
  });
  //---end--all mutations
  //---start-data-filtering
  const filteredActiveData = React.useMemo(() => {
    let result = computedData.filter((item) => item.is_active === true);
    if (selectedCluster !== "all-clusters") {
      result = result.filter((prompt) => prompt.cluster === selectedCluster);
    }
    if (tab === "active") {
      result = result.filter((prompt) =>
        prompt.text.toLowerCase().includes(search.trim().toLowerCase()),
      );
    }
    return result;
  }, [computedData, search, tab, selectedCluster]);

  const filteredInactiveData = React.useMemo(() => {
    let result = computedData.filter((item) => item.is_active === false);
    if (selectedCluster !== "all-clusters") {
      result = result.filter((prompt) => prompt.cluster === selectedCluster);
    }
    if (tab === "inactive") {
      result = result.filter((prompt) =>
        prompt.text.toLowerCase().includes(search.trim().toLowerCase()),
      );
    }
    return result;
  }, [computedData, search, tab, selectedCluster]);

  const filteredSuggestedData = React.useMemo(() => {
    let result = suggestedPromptsData;
    if (selectedCluster !== "all-clusters") {
      result = result.filter((prompt) => prompt.clusterId === selectedCluster);
    }
    if (tab === "suggested") {
      return result.filter((prompt) =>
        prompt.text.toLowerCase().includes(search.trim().toLowerCase()),
      );
    }
    return result;
  }, [suggestedPromptsData, search, selectedCluster, tab]);

  const summaryData = React.useMemo(() => {
    if (tab === "active") return filteredActiveData;
    if (tab === "inactive") return filteredInactiveData;
    return filteredSuggestedData;
  }, [tab, filteredActiveData, filteredInactiveData, filteredSuggestedData]);

  const getMetric = (prompt: any, key: "visibility" | "position") => {
    if (prompt && typeof prompt === "object" && key in prompt) {
      return (prompt as any)[key];
    }
    return undefined;
  };

  const avgVisibility = React.useMemo(() => {
    const values = summaryData
      .map((prompt) => getMetric(prompt, "visibility"))
      .filter((value) => typeof value === "number") as number[];
    if (!values.length) return null;
    const sum = values.reduce((acc, value) => acc + value, 0);
    return sum / values.length;
  }, [summaryData]);

  const avgPosition = React.useMemo(() => {
    const values = summaryData
      .map((prompt) => getMetric(prompt, "position"))
      .filter((value) => typeof value === "number") as number[];
    if (!values.length) return null;
    const sum = values.reduce((acc, value) => acc + value, 0);
    return sum / values.length;
  }, [summaryData]);

  const formatAvg = (value: number | null, suffix = "") => {
    if (value === null) return "--";
    const rounded = Math.round(value * 10) / 10;
    return `${rounded}${suffix}`;
  };
  //---end-data-filtering
  //---start-handlers

  const patchPromptCache = React.useCallback(
    (patchPrompt: (prompt: Prompt) => Prompt | null) => {
      queryClient.setQueryData(promptsQueryKey, (prev: unknown) => {
        if (!Array.isArray(prev)) return prev;
        return (prev as PromptCluster[])
          .map((cluster) => {
            const currentPrompts = Array.isArray(cluster.prompts)
              ? cluster.prompts
              : [];
            const nextPrompts = currentPrompts
              .map((prompt) => patchPrompt(prompt as Prompt))
              .filter((prompt): prompt is Prompt => Boolean(prompt));
            return {
              ...cluster,
              prompts: nextPrompts,
            };
          })
          .filter((cluster) => (cluster.prompts?.length || 0) > 0);
      });
      if (!selectedCountries || selectedCountries.length === 0) {
        const currentRawData = Array.isArray(rawData) ? rawData : [];
        const nextRawData = currentRawData
          .map((prompt) => patchPrompt(prompt as Prompt))
          .filter((prompt): prompt is Prompt => Boolean(prompt));
        if (!isSamePrompts(nextRawData)) {
          setRawData(nextRawData);
        }
      }
    },
    [
      queryClient,
      promptsQueryKey,
      selectedCountries,
      rawData,
      isSamePrompts,
      setRawData,
    ],
  );

  const handleDeleteSelected = async (
    table: Table<any>,
    action: "inactive" | "suggested",
    promptId: string | null,
  ) => {
    let selectedPromptsIds: string[] = [];
    if (!promptId) {
      const selectedRows = table.getSelectedRowModel().rows;
      selectedPromptsIds = selectedRows.map((row) => row.original.id);
    } else {
      selectedPromptsIds = [promptId];
    }

    try {
      await Promise.all(
        selectedPromptsIds.map((id: string) =>
          (action === "suggested"
            ? deleteSuggestedMutation
            : deleteMutation
          ).mutateAsync(id),
        ),
      );

      if (action !== "suggested" && selectedPromptsIds.length) {
        const selectedIds = new Set(selectedPromptsIds);
        patchPromptCache((prompt) =>
          selectedIds.has(prompt.id) ? null : prompt,
        );
      }

      queryClient.invalidateQueries({
        predicate: (query: { queryKey: any }) => {
          return ["prompts", "limits"].includes(query.queryKey[0]);
        },
      });
      if (action === "suggested") {
        queryClient.invalidateQueries({ queryKey: ["suggestedPrompts"] });
      }
      table.resetRowSelection();
      toast.success(
        `The prompt(s) ${action === "suggested" ? "rejected" : "deleted"}`,
        {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        },
      );
    } catch (err) {
      table.resetRowSelection();
      toast.error(
        `Couldn't ${action === "suggested" ? "reject" : "delete"} prompt(s)!`,
        {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        },
      );
    }
  };

  const handleStatusChangeBulk = async (
    status: boolean,
    currentTable: Table<any>,
  ) => {
    const selectedRows = currentTable.getSelectedRowModel().rows;
    const selectedPrompts = selectedRows.map((row) => {
      return {
        text: row.original.text,
        country: row.original.country,
        id: row.original.id,
        isActive: status,
      };
    });

    try {
      await Promise.all(
        selectedPrompts.map((prompt) =>
          changeStatusMutation.mutateAsync(prompt),
        ),
      );
      if (selectedPrompts.length) {
        const selectedIds = new Set(selectedPrompts.map((prompt) => prompt.id));
        patchPromptCache((prompt) => {
          if (!selectedIds.has(prompt.id)) return prompt;
          return {
            ...prompt,
            is_active: status,
          };
        });
      }
      queryClient.invalidateQueries({
        predicate: (query: { queryKey: any }) => {
          return ["prompts", "limits"].includes(query.queryKey[0]);
        },
      });
      currentTable.resetRowSelection();
      toast.success(t("legacy.the.prompt.s.status.has.been.changed.1dew62z"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Trash2 className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch (err: any) {
      currentTable.resetRowSelection();
      toast.error(
        err.response?.data.message ||
          "Couldn't change the status of the prompt(s)!",
        {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        },
      );
    }
  };

  const resolveUiLocation = React.useCallback(
    (location: "prompts_page" | "empty_state"): SuggestionUiLocation => {
      const guideTarget = searchParams.get("guideTarget");
      if (guideTarget && guideTarget.toLowerCase().includes("suggested")) {
        return "onboarding_guide";
      }
      return location;
    },
    [searchParams],
  );

  const captureBlockedEvent = React.useCallback(
    ({
      uiLocation,
      mode,
      clusterId,
      blockedState,
    }: {
      uiLocation: SuggestionUiLocation;
      mode: SuggestedPromptsGenerateMode;
      clusterId: string | null;
      blockedState: SuggestionBlockedState;
    }) => {
      capturePosthogEvent("suggested_prompts_blocked", {
        mode,
        cluster_id: clusterId,
        reason_blocked: blockedState.reason,
        remaining_quota: blockedState.remainingQuota,
        cap_today: blockedState.capToday,
        generated_today: blockedState.generatedToday,
        requested_count: CHUNK_SIZE,
        ui_location: uiLocation,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
      });
    },
    [resolvedBrandId, resolvedOrgId],
  );

  const handleSuggestTooltipOpen = React.useCallback(
    (location: "prompts_page" | "empty_state") => (open: boolean) => {
      if (!open || !activeBlockedState) return;
      captureBlockedEvent({
        uiLocation: resolveUiLocation(location),
        mode: generateMode,
        clusterId: generateClusterId,
        blockedState: activeBlockedState,
      });
    },
    [
      activeBlockedState,
      captureBlockedEvent,
      generateClusterId,
      generateMode,
      resolveUiLocation,
    ],
  );

  const handleGenerate = async (location: "prompts_page" | "empty_state") => {
    const brandId =
      resolvedBrandId ||
      (currentBrand as any)?.brandId ||
      (currentBrand as any)?.id;
    if (!brandId) return;
    const mode = generateMode;
    const clusterId = generateClusterId;
    const uiLocation = resolveUiLocation(location);
    capturePosthogEvent("suggested_prompts_click", {
      mode,
      cluster_id: clusterId,
      remaining_quota: hasRemainingQuota ? remainingQuota : undefined,
      requested_count: CHUNK_SIZE,
      ui_location: uiLocation,
      brand_id: brandId,
      org_id: resolvedOrgId,
    });

    if (activeBlockedState) {
      captureBlockedEvent({
        uiLocation,
        mode,
        clusterId,
        blockedState: activeBlockedState,
      });
      return;
    }

    let response: SuggestedPromptsGenerateResponse;
    try {
      response = await generatedPromptsQuery.mutateAsync({
        brandId,
        selectedClusterId: clusterId,
        mode,
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message || "Couldn't generate prompts!";
      toast.error(message, {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }

    const scopeKey = getScopeKey(mode, clusterId);
    if (!response?.ok && response?.status === "blocked") {
      const blockedState: SuggestionBlockedState = {
        reason: response.reason,
        remainingQuota: Number(response.remaining_quota || 0),
        capToday: Number(response.cap_today || 0),
        generatedToday: Number(response.generated_today || 0),
      };
      setBlockedByScope((prev) => ({
        ...prev,
        [scopeKey]: blockedState,
      }));
      captureBlockedEvent({
        uiLocation,
        mode,
        clusterId,
        blockedState,
      });
      return;
    }

    if (!response?.ok || response?.status !== "ok") {
      toast.error((response as any)?.error || "Couldn't generate prompts!", {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }

    setBlockedByScope((prev) => {
      const next = { ...prev };
      delete next[scopeKey];
      return next;
    });

    capturePosthogEvent("suggested_prompts_generated", {
      mode,
      cluster_id: clusterId,
      generated_count: response.generated_count,
      remaining_quota_after: response.remaining_quota_after,
      cap_today: response.cap_today,
      generated_today: response.generated_today,
      brand_id: brandId,
      org_id: resolvedOrgId,
    });

    if (!response.generated_count) {
      toast.error(t("legacy.no.prompts.generated.1bx5qyl"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["suggestedPrompts"] });
    toast.success(t("legacy.prompts.generated.successfully.15xf4ut"), {
      icon: (
        <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
          <Check className="w-4 h-4 text-black" />
        </div>
      ),
      position: "bottom-right",
    });
  };

  const handleClusterUpdate = async (clusterId: string) => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (!selectedRows.length) return;
    const updates = selectedRows.map((row) => ({
      text: row.original.text,
      country: row.original.country,
      id: row.original.id,
      isActive: row.original.is_active,
      clusterId,
    }));

    try {
      await Promise.all(
        updates.map((prompt) => changeStatusMutation.mutateAsync(prompt)),
      );
      queryClient.invalidateQueries({
        predicate: (query: { queryKey: any }) => {
          return ["prompts"].includes(query.queryKey[0]);
        },
      });
      toast.success(t("legacy.the.prompt.s.cluster.was.updated.m6ktt"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      table.resetRowSelection();
    } catch (err: any) {
      toast.error(
        err.response?.data.message ||
          "Couldn't update the cluster for prompt(s)!",
        {
          position: "bottom-right",
        },
      );
    }
  };

  const handleAdd = async (promptAdd: Addprompt | null) => {
    let selectedPrompts: Addprompt[] = [];
    const clusterId =
      selectedCluster !== "all-clusters" ? selectedCluster : undefined;
    const clusterTitle =
      selectedCluster === "all-clusters" ? "No Clusters" : undefined;

    if (!promptAdd) {
      const selectedRows = suggestedPromptsTable.getSelectedRowModel().rows;
      selectedPrompts = selectedRows.map((row) => {
        let prompt: Addprompt = {
          country: row.original.country,
          text: row.original.text,
          isActive: true,
          brandId: currentBrand.brandId || "",
          clusterId,
          clusterTitle,
        };
        if (row.original.id) {
          prompt.suggestedPromptId = row.original.id;
        }
        return prompt;
      });
    } else {
      let currPrompt: Addprompt = {
        country: promptAdd.country,
        text: promptAdd.text,
        isActive: true,
        brandId: currentBrand.brandId || "",
        clusterId: promptAdd.clusterId,
        clusterTitle: promptAdd.clusterTitle,
      };
      if (promptAdd.id) {
        currPrompt.suggestedPromptId = promptAdd.id;
      }
      selectedPrompts = [currPrompt];
    }

    try {
      await Promise.all(
        selectedPrompts.map((prompt) => addMutation.mutateAsync(prompt)),
      );
      queryClient.invalidateQueries({
        predicate: (query: { queryKey: any }) => {
          return ["prompts", "limits", "suggestedPrompts"].includes(
            query.queryKey[0],
          );
        },
      });
      suggestedPromptsTable.resetRowSelection();
      toast.success(t("legacy.the.prompt.s.has.been.added.to.active.5cxdt6"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch (err: any) {
      suggestedPromptsTable.resetRowSelection();
      toast.error(
        err.response?.data.message || "Couldn't add prompt(s) to active!",
        {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        },
      );
    }
  };
  //---end-handlers
  //---start-tables
  const inactiveTable = useReactTable({
    data: filteredInactiveData,
    columns: PromptsPageColumns,
    autoResetPageIndex: false,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveRowSelection,
    onPaginationChange: setInactivePagination,
    state: {
      pagination: inactivePagination,
      sorting,
      columnFilters,
      rowSelection: inactiveRowSelection,
    },
    meta: {
      isPromptMetricLoading: () => false,
    },
  });

  const table = useReactTable({
    data: filteredActiveData,
    columns: PromptsPageColumns,
    autoResetPageIndex: false,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setActivePagination,
    state: {
      pagination: activePagination,
      sorting,
      columnFilters,
      rowSelection,
    },
    meta: {
      isPromptMetricLoading: (prompt: any) => isPromptMetricLoading(prompt),
    },
  });

  const suggestedPromptsTable = useReactTable({
    data: filteredSuggestedData,
    columns: getSuggestedColumns(
      (id) => handleDeleteSelected(suggestedPromptsTable, "suggested", id),
      (prompt: Addprompt) => handleAdd(prompt),
    ),
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setSuggestedPromptsRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setSuggestedPagination,
    state: {
      pagination: suggestedPagination,
      rowSelection: suggestedPromptsRowSelection,
    },
  });
  //---end-tables

  useEffect(() => {
    const allCountries = new Set(rawData.map((el) => el.country));
    //@ts-ignore
    getCountriesOption([...allCountries]);
  }, [rawData]);

  useEffect(() => {
    inactiveTable.resetRowSelection();
    table.resetRowSelection();
    setSearch("");
  }, [tab]);

  useEffect(() => {
    if (!pendingBoostClusterId || !pendingBoostBrandId) return;
    const activeCluster = clustersData.find(
      (cluster) => cluster.id === pendingBoostClusterId,
    );
    const boostStatus = activeCluster?.boostStatus || "";
    if (boostStatus === "ready") {
      if (selectedCluster !== pendingBoostClusterId) {
        // setAnimating(false);
        setPendingBoostClusterId(null);
        setPendingBoostBrandId(null);
        return;
      }
      // setAnimating(false);
      setPendingBoostClusterId(null);
      setPendingBoostBrandId(null);
      navigate(buildClusterActionsPath(pendingBoostBrandId, selectedCluster));
      return;
    }
    if (boostStatus === "error") {
      // setAnimating(false);
      setPendingBoostClusterId(null);
      setPendingBoostBrandId(null);
      toast.error(t("legacy.boost.failed.please.try.again.u6hpxu"), {
        position: "bottom-right",
      });
    }
  }, [
    clustersData,
    navigate,
    buildClusterActionsPath,
    pendingBoostBrandId,
    pendingBoostClusterId,
    selectedCluster,
  ]);

  const handleViewTasks = () => {
    if (selectedCluster === "all-clusters") return;
    const brandId = (currentBrand as any)?.brandId || (currentBrand as any)?.id;
    navigate(buildClusterActionsPath(brandId, selectedCluster));
  };

  const handleRegenerate = async () => {
    // if (animating) return;
    if (selectedCluster === "all-clusters") {
      toast.error(t("legacy.select.a.cluster.to.boost.prompts.1df1psx"), {
        position: "bottom-right",
      });
      return;
    }
    const brandId = (currentBrand as any)?.brandId || (currentBrand as any)?.id;
    if (!brandId) {
      toast.error(t("legacy.select.a.brand.to.boost.prompts.4092r4"), {
        position: "bottom-right",
      });
      return;
    }
    const activeCluster = clustersData.find(
      (cluster) => cluster.id === selectedCluster,
    );
    const boostStatus = String(activeCluster?.boostStatus || "")
      .trim()
      .toLowerCase();
    const isRunning = isClusterActivelyRunningBoost(activeCluster);
    const isRegenerateAction = boostStatus === "ready" && !isRunning;
    const isCooldownBlocked = shouldBlockBoostByCooldown(
      Boolean(activeCluster?.boostLocked),
      isRegenerateAction,
    );
    if (isTrialLockedCluster) {
      setIsBoostLimitModalOpen(true);
      capturePosthogEvent("boost_cluster_blocked_trial_limit", {
        requested_cluster_id: selectedCluster,
        trial_boosted_cluster_id: trialAllowedClusterId,
        brand_id: brandId,
        org_id: resolvedOrgId,
        source: boostEventSource,
      });
      return;
    }
    if (isRunning) {
      toast.error(
        t("legacy.boost.is.already.running.for.this.cluster.1rh5dia"),
        {
          position: "bottom-right",
        },
      );
      return;
    }
    if (isCooldownBlocked) {
      toast.error(boostBlockedByCooldownReason, {
        position: "bottom-right",
      });
      return;
    }
    if (boostBlockedByPromptRuns) {
      toast.error(boostBlockedByPromptRunsReason, {
        position: "bottom-right",
      });
      return;
    }
    capturePosthogEvent("boost_cluster_click", {
      cluster_id: selectedCluster,
      cluster_name: activeCluster?.title || null,
      plan_type: planType,
      brand_id: brandId,
      org_id: resolvedOrgId,
      source: boostEventSource,
    });
    capturePosthogEvent("cluster_boost_clicked", {
      brand_id: brandId,
      cluster_id: selectedCluster,
      cluster_name: activeCluster?.title || "unknown",
      entry_point: "prompts_page",
    });
    try {
      const res = await promptsAPI.boostPrompts({
        brandId,
        clusterId: selectedCluster,
        providers:
          selectedProviders && selectedProviders.length
            ? selectedProviders
            : undefined,
        days: resolveBoostDays(timeRange, 30),
      });
      if (!res?.ok) {
        const blockedInfo = getTrialBoostBlockedInfo(res);
        if (blockedInfo.blocked) {
          setIsBoostLimitModalOpen(true);
          capturePosthogEvent("boost_cluster_blocked_trial_limit", {
            requested_cluster_id: selectedCluster,
            trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
            brand_id: brandId,
            org_id: resolvedOrgId,
            source: boostEventSource,
          });
          return;
        }
        throw new Error(res?.message || "Failed to start boost.");
      }
      navigate(buildClusterActionsPath(brandId, selectedCluster));
      setPendingBoostClusterId(selectedCluster);
      setPendingBoostBrandId(brandId);
    } catch (err: any) {
      const payload = err?.response?.data || {};
      const blockedInfo = getTrialBoostBlockedInfo(payload);
      if (blockedInfo.blocked) {
        setIsBoostLimitModalOpen(true);
        capturePosthogEvent("boost_cluster_blocked_trial_limit", {
          requested_cluster_id: selectedCluster,
          trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
          brand_id: brandId,
          org_id: resolvedOrgId,
          source: boostEventSource,
        });
        return;
      }
      setPendingBoostClusterId(null);
      setPendingBoostBrandId(null);
      toast.error(
        payload?.message || err?.message || "Failed to start boost.",
        {
          position: "bottom-right",
        },
      );
    }
  };

  const calcTotal = (sCluster: string, currtab: string) => {
    const isActiveTab = currtab === "active";
    if (currtab === "suggested") {
      if (!sCluster) {
        return suggestedPromptsData.length;
      }
      return suggestedPromptsData.filter(
        (prompt) => prompt.clusterId === sCluster,
      ).length;
    }
    let result = computedData.filter((item) => item.is_active === isActiveTab);
    if (sCluster) {
      result = result.filter((prompt) => prompt.cluster === sCluster);
    }
    return result.length;
  };

  return (
    <div className="h-full pb-[28px]">
      <div className="text-sm text-[#737373] mb-4">
        <LocalizedText id="legacy.manage.and.organize.your.saved.clusters.and.prompts.qhwhwd" />
      </div>
      <div className="flex border p-3 rounded-md h-full min-h-[0px]">
        <div className="overflow-y-auto flex flex-col min-w-[264px] border mr-3 rounded-md p-3">
          <div
            onClick={() => setSelectedCluster("all-clusters")}
            className={`flex justify-between font-medium cursor-pointer text-sm p-2 rounded-md ${selectedCluster === "all-clusters" ? "bg-[#F5F5F5]" : ""}`}
          >
            <div>
              <LocalizedText id="legacy.all.clusters.1ch60zh" />
            </div>
            <div className="text-[#737373]">{calcTotal("", tab)}</div>
          </div>
          <Separator className="my-1 h-px w-full bg-border" />

          {clustersData.length ? (
            <div className="flex relative flex-col justify-between h-full">
              <div>
                {clustersData.map((item) => {
                  const isOpen = openId === item.id;
                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelectedCluster(item.id)}
                      className={`flex rounded-md justify-between p-2 items-center hover:bg-[#F5F5F5] ${selectedCluster === item.id ? "bg-[#F5F5F5]" : ""}`}
                    >
                      <div
                        onPointerEnter={(event) => {
                          const titleElement =
                            event.currentTarget.querySelector(
                              '[data-cluster-title="true"]',
                            ) as HTMLDivElement | null;
                          const isTruncated = Boolean(
                            titleElement &&
                              titleElement.scrollWidth >
                                titleElement.clientWidth,
                          );
                          if (!isTruncated) {
                            if (openTimeoutRef.current) {
                              clearTimeout(openTimeoutRef.current);
                            }
                            setOpenId(null);
                            return;
                          }
                          openTimeoutRef.current = setTimeout(() => {
                            setOpenId(item.id);
                          }, 800);
                        }}
                        onPointerLeave={() => {
                          if (openTimeoutRef.current) {
                            clearTimeout(openTimeoutRef.current);
                          }
                          setOpenId(null);
                        }}
                        className="font-medium text-sm max-w-[195px] w-full cursor-pointer"
                      >
                        <Popover open={isOpen}>
                          <PopoverTrigger asChild>
                            <div
                              data-cluster-title="true"
                              className="w-full truncate"
                            >
                              {item.title}
                            </div>
                          </PopoverTrigger>

                          <PopoverContent
                            onClick={(e) => e.stopPropagation()}
                            className="transition-all duration-200 ease-out w-auto p-3 text-sm max-w-xs break-words"
                          >
                            {item.title}
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="text-[#737373] text-sm">
                        {isFetching ||
                        addMutation.isPending ||
                        changeStatusMutation.isPending ||
                        deleteMutation.isPending ? (
                          <div className="p-1 border rounded-md">
                            <Spinner
                              className="text-[#FF5E00]"
                              variant="circle-filled"
                              size={16}
                            />
                          </div>
                        ) : (
                          <div className="group relative flex items-center justify-center rounded-md cursor-pointer">
                            {/* Number */}
                            <span className="transition-opacity p-1 duration-200 group-hover:opacity-0">
                              {calcTotal(item.id, tab)}
                            </span>

                            {/* Icon */}
                            <span className="absolute opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                              <ClusterAction
                                cluster={item}
                                prompts={filteredActiveData}
                                setSelectedCluster={setSelectedCluster}
                              ></ClusterAction>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <ClusterAdd
                currentBrand={currentBrand}
                clustersData={clustersData}
                onPromptAdd={(payload) => handleAdd(payload)}
              ></ClusterAdd>
            </div>
          ) : isFetching ? (
            <div className="relative h-[150px] flex flex-col gap-2">
              <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                  <Spinner
                    className="text-[#FF5E00]"
                    variant="circle"
                  ></Spinner>
                </div>
                <div>
                  <LocalizedText id="legacy.processing.3jbdvy" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex grow">
              <div className="flex flex-col gap-3 items-center my-auto">
                <div className="p-2 rounded-md bg-[#F5F5F5]">
                  <Info size={24} strokeWidth={1.5} />
                </div>
                <div className="font-medium text-lg text-[#0A0A0A]">
                  <LocalizedText id="legacy.no.clusters.yet.1nsyylx" />
                </div>
                <div className="text-sm text-center leading-5 text-[#737373]">
                  <LocalizedText id="legacy.create.clusters.to.group.related.prompts.and.keep.1ewepbw" />
                </div>
                <ClusterAdd
                  orangeButton={true}
                  currentBrand={currentBrand}
                  clustersData={clustersData}
                  onPromptAdd={(payload) => handleAdd(payload)}
                ></ClusterAdd>
              </div>
            </div>
          )}
        </div>
        <div className="w-full overflow-auto rounded-md border p-4">
          <div>
            <div className="flex-col gap-4 items-start xl:flex-row flex xl:items-center justify-between pb-4">
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <div className="flex justify-between w-full items-center flex-wrap gap-2">
                  <TabsList>
                    <TabsTrigger className="cursor-pointer" value="active">
                      <LocalizedText id="legacy.active.1530k7d" />{" "}
                    </TabsTrigger>
                    <TabsTrigger className="cursor-pointer" value="suggested">
                      <LocalizedText id="legacy.suggested.vyozz4" />{" "}
                    </TabsTrigger>
                    <TabsTrigger className="cursor-pointer" value="inactive">
                      <LocalizedText id="legacy.inactive.1q9yyem" />{" "}
                    </TabsTrigger>
                  </TabsList>
                  <div className="flex-wrap lg:flex-nowrap flex gap-2 items-center text-sm">
                    <SearchExportGenerate
                      search={search}
                      refetch={() => handleGenerate("prompts_page")}
                      tab={tab}
                      setSearch={setSearch}
                      suggestDisabled={suggestGenerationDisabled}
                      suggestTooltipMessage={suggestGenerationTooltip}
                      onSuggestTooltipOpenChange={handleSuggestTooltipOpen(
                        "prompts_page",
                      )}
                      data={
                        tab === "active"
                          ? filteredActiveData
                          : tab === "inactive"
                            ? filteredInactiveData
                            : filteredSuggestedData
                      }
                      isGeneratedLoading={generatedPromptsQuery.isPending}
                      table={
                        tab === "active"
                          ? table
                          : tab === "inactive"
                            ? inactiveTable
                            : suggestedPromptsTable
                      }
                    />
                    {tab === "active" && (
                      <AddPromptsDialog
                        clustersData={clustersData}
                        openAddPromptDialog={openAddPromptDialog}
                        cluster={
                          clustersData.find((cl) => cl.id === selectedCluster)
                            ?.title || "No Clusters"
                        }
                        setOpenAddPromptDialog={setOpenAddPromptDialog}
                        currentBrand={currentBrand}
                        addMutation={(prompt: Addprompt) => handleAdd(prompt)}
                      ></AddPromptsDialog>
                    )}
                  </div>
                </div>
                <Separator className="my-1 h-px w-full bg-border" />
                <div className="flex gap-3 justify-between min-h-[36px]">
                  {tab !== "suggested" && (
                    <div className="flex gap-3">
                      <Badge variant="outline">
                        <Eye></Eye>{" "}
                        <LocalizedText id="legacy.visibility.s925mr" />{" "}
                        <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
                          {formatAvg(avgVisibility, "%")}
                        </div>
                      </Badge>
                      <Badge variant="outline">
                        <ChartNoAxesColumnIncreasing />
                        <LocalizedText id="legacy.position.1k9t5h8" />{" "}
                        <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
                          {formatAvg(avgPosition)}
                        </div>
                      </Badge>
                    </div>
                  )}

                  {selectedCluster !== "all-clusters" &&
                    typeof avgVisibility === "number" &&
                    avgVisibility >= 0 &&
                    avgVisibility < 100 &&
                    (() => {
                      const activeCluster = clustersData.find(
                        (cluster) => cluster.id === selectedCluster,
                      );
                      const boostStatus = String(
                        activeCluster?.boostStatus || "",
                      )
                        .trim()
                        .toLowerCase();
                      const isRunning =
                        isClusterActivelyRunningBoost(activeCluster);
                      const isReady = boostStatus === "ready" && !isRunning;
                      const isError = boostStatus === "error";
                      const shouldShowProcessingState = isRunning;

                      const primaryLabel = shouldShowProcessingState
                        ? t("prompts.action.processing")
                        : isError
                          ? t("prompts.action.retryBoost")
                          : t("prompts.action.boost");
                      const isBoostBlockedByCooldown =
                        shouldBlockBoostByCooldown(
                          Boolean(activeCluster?.boostLocked),
                          isReady,
                        );
                      const isBoostBlockedByState =
                        !shouldShowProcessingState &&
                        (boostBlockedByPromptRuns || isBoostBlockedByCooldown);
                      const trialLockedReason =
                        t("prompts.boost.trialLimit");
                      const canRunBoostAction =
                        !shouldShowProcessingState &&
                        !isTrialLockedCluster &&
                        !isBoostBlockedByState;
                      const canClickBoostAction =
                        canRunBoostAction || isTrialLockedCluster;
                      const processingStatusMessage = isRunning
                        ? t("prompts.boost.processing")
                        : "";
                      const blockedBoostTooltip = isTrialLockedCluster
                        ? trialLockedReason
                        : shouldShowProcessingState
                          ? processingStatusMessage ||
                            "Boost is generating actions. This can take a few minutes."
                          : isBoostBlockedByCooldown
                            ? getCooldownBlockedMessage(
                                activeCluster?.boostUnlockAt,
                                activeCluster?.boostCooldownDays,
                              )
                            : boostBlockedByPromptRunsReason ||
                              "Give your prompts a bit more time to run.";
                      const showBoostAnimation = canRunBoostAction;

                      return (
                        <div className="flex gap-1">
                          {isError &&
                            tab === "active" &&
                            selectedCluster !== "all-clusters" &&
                            activeCluster?.prompts.length && (
                              <div className="text-sm bg-[#FEEBE7] p-1 rounded-lg flex items-center">
                                <Info
                                  className="text-[#DD4425] mr-1"
                                  size={14}
                                />
                                <LocalizedText id="legacy.we.couldnt.boost.prompts.please.try.again.1clvnk8" />
                              </div>
                            )}

                          {tab === "active" &&
                            (Number(avgVisibility) < 100 ||
                              (Number(avgVisibility) === 100 &&
                                (isRunning || isReady || isError))) &&
                            selectedCluster !== "all-clusters" &&
                            Boolean(filteredActiveData.length) && (
                              <div className="flex items-center gap-2">
                                {shouldShowProcessingState ? (
                                  <div className="text-sm bg-[#FEEBE7] p-1 rounded-lg flex items-center">
                                    <Info
                                      className="text-[#DD4425] mr-1"
                                      size={14}
                                    />
                                    {processingStatusMessage}
                                  </div>
                                ) : null}
                                {isReady ? (
                                  <>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                          <Button
                                            variant="outline"
                                            data-onboarding-target="prompts-boost-button"
                                            className={`h-10 rounded-md px-4 shadow-none ${
                                              !canClickBoostAction
                                                ? "cursor-not-allowed border-[#E5E5E5] bg-[#F5F5F5] text-[#A3A3A3] hover:bg-[#F5F5F5]"
                                                : "cursor-pointer border-[#D4D4D4] bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"
                                            }`}
                                            onClick={handleRegenerate}
                                            disabled={!canClickBoostAction}
                                            title={
                                              !canClickBoostAction
                                                ? blockedBoostTooltip
                                                : ""
                                            }
                                          >
                                            <img
                                              src={repeatIcon}
                                              alt=""
                                              className="h-4 w-4"
                                            />
                                            <LocalizedText id="legacy.regenerate.1ih22qh" />
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      {!canClickBoostAction ? (
                                        <TooltipContent
                                          side="top"
                                          className="max-w-[220px] whitespace-normal break-words text-center"
                                        >
                                          {blockedBoostTooltip}
                                        </TooltipContent>
                                      ) : null}
                                    </Tooltip>
                                    <Button
                                      className="h-10 cursor-pointer rounded-md bg-[#0A0A0A] px-4 text-[#FAFAFA] hover:bg-[#171717]"
                                      onClick={handleViewTasks}
                                    >
                                      <LocalizedText id="legacy.view.tasks.17o9mnq" />
                                      <img
                                        src={viewTasksArrowRight}
                                        alt=""
                                        className="h-4 w-4"
                                      />
                                    </Button>
                                  </>
                                ) : (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className={
                                          showBoostAnimation
                                            ? "relative inline-block"
                                            : "inline-flex"
                                        }
                                      >
                                        {showBoostAnimation ? (
                                          <svg
                                            className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)] pointer-events-none"
                                            viewBox="0 0 100 40"
                                            preserveAspectRatio="none"
                                          >
                                            <rect
                                              x="2"
                                              y="2"
                                              width="96"
                                              height="36"
                                              rx="6"
                                              fill="none"
                                              stroke="#FF5E00"
                                              strokeWidth="2"
                                              strokeDasharray="20 234"
                                              strokeLinecap="round"
                                              style={{ filter: "blur(1.5px)" }}
                                              className="border-loader"
                                            />
                                            <rect
                                              x="2"
                                              y="2"
                                              width="96"
                                              height="36"
                                              rx="6"
                                              fill="none"
                                              stroke="#FF5E00"
                                              strokeWidth="2"
                                              strokeDasharray="20 234"
                                              strokeLinecap="round"
                                              className="border-loader"
                                            />
                                          </svg>
                                        ) : null}
                                        <Button
                                          data-onboarding-target="prompts-boost-button"
                                          className={`h-10 rounded-md px-4 ${
                                            !canClickBoostAction
                                              ? shouldShowProcessingState
                                                ? "cursor-not-allowed bg-[#0A0A0A] text-[#FAFAFA] hover:bg-[#0A0A0A]"
                                                : "cursor-not-allowed bg-[#E5E5E5] text-[#A3A3A3] hover:bg-[#E5E5E5]"
                                              : "cursor-pointer bg-[#0A0A0A] text-[#FAFAFA] hover:bg-[#171717]"
                                          } ${showBoostAnimation ? "relative z-10" : ""}`}
                                          onClick={handleRegenerate}
                                          disabled={!canClickBoostAction}
                                          title={
                                            !canClickBoostAction &&
                                            !shouldShowProcessingState
                                              ? blockedBoostTooltip
                                              : ""
                                          }
                                        >
                                          {shouldShowProcessingState ? (
                                            <Spinner
                                              className="text-[#FAFAFA]"
                                              size={16}
                                            />
                                          ) : isError ? (
                                            <img
                                              src={repeatIcon}
                                              alt=""
                                              className="h-4 w-4"
                                            />
                                          ) : null}
                                          {primaryLabel}
                                        </Button>
                                      </span>
                                    </TooltipTrigger>
                                    {!canClickBoostAction &&
                                    !shouldShowProcessingState ? (
                                      <TooltipContent
                                        side="top"
                                        className="max-w-[220px] whitespace-normal break-words text-center"
                                      >
                                        {blockedBoostTooltip}
                                      </TooltipContent>
                                    ) : null}
                                  </Tooltip>
                                )}
                              </div>
                            )}
                        </div>
                      );
                    })()}
                </div>

                <TabsContent value="active">
                  <PromptsTable
                    table={table}
                    highlightTargetPromptId={guidePromptId}
                    isFiltered={
                      !!data?.length &&
                      !!search.trim().length &&
                      tab === "active"
                    }
                    limitPrompts={limitPrompts}
                    tableAction={() => setOpenAddPromptDialog(true)}
                    onOpenPromptDetails={(prompt: any) => {
                      const promptId = String(prompt?.id || "");
                      if (!promptId) return;
                      capturePosthogEvent("prompt_details_opened", {
                        brand_id: resolvedBrandId,
                        prompt_id: promptId,
                        cluster_id: String(prompt?.cluster || ""),
                        platform: toPlatformValue(selectedProviders || []),
                        country: String(prompt?.country || "")
                          .trim()
                          .toUpperCase(),
                      });
                      setDetailsPromptId(promptId);
                      setIsPromptDetailsOpen(true);
                    }}
                    isLoading={shouldShowPromptsTableLoading}
                  ></PromptsTable>
                </TabsContent>
                <TabsContent value="suggested">
                  <ExtendedTable
                    limitPrompts={limitPrompts}
                    noDataText={{
                      title:
                        !!suggestedPromptsData.length && !!search.trim().length
                          ? t("common.allPromptsFiltered")
                          : t("common.noPromptsYet"),
                      description:
                        !!suggestedPromptsData.length && !!search.trim().length
                          ? t("common.clearSearchToSeePrompts")
                          : t("common.noPromptsGenerated"),
                    }}
                    action={
                      !!suggestedPromptsData.length && !!search.trim().length
                        ? null
                        : {
                            title: t("prompts.action.getSuggested"),
                            actionFn: () => handleGenerate("empty_state"),
                            disabled: suggestGenerationDisabled,
                            tooltipMessage: suggestGenerationTooltip,
                            onTooltipOpenChange:
                              handleSuggestTooltipOpen("empty_state"),
                          }
                    }
                    loading={
                      isSuggestedPromptsLoading ||
                      isSuggestedPromptsFetching ||
                      generatedPromptsQuery.isPending ||
                      addMutation.isPending ||
                      deleteSuggestedMutation.isPending
                    }
                    table={suggestedPromptsTable}
                    columns={5}
                  ></ExtendedTable>
                </TabsContent>
                <TabsContent value="inactive">
                  <PromptsTable
                    table={inactiveTable}
                    isFiltered={
                      !!data?.length &&
                      !!search.trim().length &&
                      tab === "inactive"
                    }
                    limitPrompts={limitPrompts}
                    tableAction={() => setOpenAddPromptDialog(true)}
                    onOpenPromptDetails={(prompt: any) => {
                      const promptId = String(prompt?.id || "");
                      if (!promptId) return;
                      capturePosthogEvent("prompt_details_opened", {
                        brand_id: resolvedBrandId,
                        prompt_id: promptId,
                        cluster_id: String(prompt?.cluster || ""),
                        platform: toPlatformValue(selectedProviders || []),
                        country: String(prompt?.country || "")
                          .trim()
                          .toUpperCase(),
                      });
                      setDetailsPromptId(promptId);
                      setIsPromptDetailsOpen(true);
                    }}
                    isLoading={shouldShowPromptsTableLoading}
                  ></PromptsTable>
                </TabsContent>
              </Tabs>
            </div>
          </div>
          {tab === "active" ? (
            <PromptsActive
              rowSelection={rowSelection}
              table={table}
              options={clustersData}
              selectedOption={selectedCluster}
              handler={handleStatusChangeBulk}
              handlePrompClusterChange={handleClusterUpdate}
            ></PromptsActive>
          ) : tab === "inactive" ? (
            <PromptsInactive
              inactiveRowSelection={inactiveRowSelection}
              inactiveTable={inactiveTable}
              handleStatusChange={handleStatusChangeBulk}
              handleDeleteSelected={handleDeleteSelected}
            ></PromptsInactive>
          ) : (
            <ActionsSuggested
              rowSelection={suggestedPromptsRowSelection}
              table={suggestedPromptsTable}
              noConfirmation={true}
              handleStatusChange={handleAdd}
              handleDeleteSelected={handleDeleteSelected}
            ></ActionsSuggested>
          )}
        </div>
      </div>
      {selectedPromptForDetails ? (
        <PromptsDetails
          key={selectedPromptForDetails.id}
          prompt={selectedPromptForDetails}
          currentBrand={currentBrand}
          globalSelectedProviders={selectedProviders}
          globalTimeRange={timeRange}
          globalDateRange={dateRange}
          isOpen={isPromptDetailsOpen}
          onTimeRangeChange={setTimeRange}
          onDateRangeChange={setDateRange}
          onOpenChange={(open) => {
            setIsPromptDetailsOpen(open);
            if (!open) {
              setDetailsPromptId(null);
            }
          }}
        />
      ) : null}
      <Dialog
        open={isBoostLimitModalOpen}
        onOpenChange={setIsBoostLimitModalOpen}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[425px] gap-4 border-[#E5E5E5] p-4 sm:max-w-[425px] sm:p-4"
        >
          <DialogHeader className="items-center gap-2 text-center sm:text-center">
            <TriangleAlert size={28} className="text-[#FF5E00]" />
            <DialogTitle className="text-xl leading-7 font-semibold">
              <LocalizedText id="legacy.boost.limit.reached.6n7wi1" />
            </DialogTitle>
            <DialogDescription className="mx-auto max-w-[375px] text-center leading-6">
              <span className="block">
                <LocalizedText id="legacy.your.trial.includes.boost.actions.for.1.cluster.10li0v" />
              </span>
              <span className="block">
                <LocalizedText id="legacy.upgrade.to.boost.more.clusters.and.unlock.full.6gk8jp" />
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-hidden rounded-lg border border-[#E5E5E5]">
            <div className="divide-y divide-[#E5E5E5] px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ChartNoAxesColumnIncreasing size={16} />
                  <LocalizedText id="legacy.boost.unlimited.clusters.1ayeniu" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ScanEye size={16} />
                  <LocalizedText id="legacy.full.visibility.analytics.19jp1i" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <Zap size={16} />
                  <LocalizedText id="legacy.higher.usage.limits.1tpejxb" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
            </div>
            <div className="border-t border-[#E5E5E5] px-4 py-1 text-center text-xs text-[#737373]">
              <LocalizedText id="legacy.and.more.features.h73j0y" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              className="cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff8138]"
              onClick={() => {
                const brandSearch = resolvedBrandId
                  ? `?brandId=${resolvedBrandId}`
                  : "";
                capturePosthogEvent("upgrade_clicked", {
                  modal: "trial_boost_limit",
                  brand_id: resolvedBrandId,
                  org_id: resolvedOrgId,
                });
                setIsBoostLimitModalOpen(false);
                navigate(`/dashboard/plans-pricing${brandSearch}`);
              }}
            >
              <LocalizedText id="legacy.see.plans.and.upgrade.11ooxj4" />
            </Button>
            <Button
              variant="outline"
              className="w-fit cursor-pointer self-center"
              onClick={() => setIsBoostLimitModalOpen(false)}
            >
              <LocalizedText id="legacy.not.now.1f5o0g6" />
            </Button>
          </div>

          <div className="text-center text-xs text-[#737373]">
            <LocalizedText id="legacy.need.help.or.a.custom.plan.16lhm8t" />{" "}
            <button
              className="text-[#0A0A0A] underline underline-offset-2"
              onClick={() => {
                setIsBoostLimitModalOpen(false);
                navigate("/dashboard/support");
              }}
            >
              <LocalizedText id="legacy.contact.us.14c3h4z" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
